import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { Form, Button, Modal, Toast } from 'react-bootstrap';
import useGroceryListIngredientStatus from '../hooks/groceryListIngredient';
import useGroceryListDifferences from '../hooks/getGroceryListDifferences';
import MassVolumeConversionPopup from './MassVolumeConversionPopup';

import { useAddPantryIngredient, useAddPantryIngredients, useUpdatePantryIngredient} from '../hooks/pantryIngredient';
import { useAuth } from '../context/AuthContext';
import '../index.css';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { getTodayLocalDateString } from '../utils/dateUtils';


const IngredientName = memo(({ children, textKey, onClick, disabled, ...props }) => {
  const spanRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasEverOverflowed, setHasEverOverflowed] = useState(false);

  useEffect(() => {
    setHasEverOverflowed(false); // Reset on text change
  }, [textKey]);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // Add a 2px buffer to prevent oscillation
      const currentlyOverflowing = el.scrollWidth > el.clientWidth + 2;
      setIsOverflowing(prev => {
        if (prev !== currentlyOverflowing) {
          return currentlyOverflowing;
        }
        return prev;
      });
      if (currentlyOverflowing) {
        setHasEverOverflowed(true);
      }
    };

    checkOverflow(); // Initial check

    // Set up ResizeObserver
    const resizeObserver = new window.ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    // Also listen for window resize (in case parent containers change)
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [children]);

  const handleClick = (e) => {
    if (!disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <span
      ref={spanRef}
      className="ms-2 text-truncate"
      onClick={handleClick}
      style={{
        textDecoration: props.lineThrough ? 'line-through' : 'none',
        maxWidth: '240px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: hasEverOverflowed ? '0.75em' : '1em', // Shrink only, reset on text change
        transition: 'font-size 0.2s',
        cursor: disabled ? 'default' : 'pointer',
        ...props.style,
      }}
    >
      {children}
    </span>
  );
});

const GroceryList = ({ groceryListId, refreshTrigger, isInPast }) => {
  // Raw data from API
  const [rawIngredients, setRawIngredients] = useState([]);
  // Stable ordered view that preserves positions during interactions
  const [stableIngredients, setStableIngredients] = useState([]);
  const [groceryListDetails, setGroceryListDetails] = useState(null);
  const { setGroceryListIngredientIsInCartStatus, setGroceryListIngredientPurchasedStatus, setMultipleGroceryListIngredientsPurchasedStatus, setGroceryListIngredientConfirmedStatus } = useGroceryListIngredientStatus();
  const { updatePantryIngredient,
        updatePantryIngredientConfirmedStatusByIngredientIdAndPantryIdAndAppUserId,
        pantryIngredientNoLongerInPantryByIngredientIdAndPantryIdAndAppUserId: removeIngredientFromPantry } = useUpdatePantryIngredient();
  // const { refreshGroceryList, isLoading: isRefreshing, error: refreshError } = useRefreshGroceryList();
  const { isAdding: isAddingSingle } = useAddPantryIngredient();
  const { addPantryIngredients, isAdding: isAddingBatch } = useAddPantryIngredients();
  const { pgUser, sessionToken } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingQuantities, setPendingQuantities] = useState({});
  const [editingQtyId, setEditingQtyId] = useState(null);
  const loadedGroceryListId = useRef(null);
  const [errorAddingToPantry, setErrorAddingToPantry] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showToast, setShowToast] = useState(false); //success toast
  const [toastMessage, setToastMessage] = useState('Grocery List Updated');
  // Use combined loading state
  const isAddingToPantry = isAddingSingle || isAddingBatch;
  const [showDifferencesModal, setShowDifferencesModal] = useState(false);
  const [differences, setDifferences] = useState([]);
  const { getDifferences, isLoading: isDifferencesLoading } = useGroceryListDifferences();
  const [showMassVolumeConversionPopup, setShowMassVolumeConversionPopup] = useState(false);
  const [selectedIngredientForConversion, setSelectedIngredientForConversion] = useState(null);

  // Helper function to format quantity as clean fractions when possible
  const formatQuantity = useCallback((num) => {
    const allowedFractions = [
      { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
      { fraction: 1/8, label: "1/8", vulgar: "⅛" },
      { fraction: 1/6, label: "1/6", vulgar: "⅙" },
      { fraction: 1/4, label: "1/4", vulgar: "¼" },
      { fraction: 1/3, label: "1/3", vulgar: "⅓" },
      { fraction: 1/2, label: "1/2", vulgar: "½" },
      { fraction: 5/8, label: "5/8", vulgar: "⅝" },
      { fraction: 2/3, label: "2/3", vulgar: "⅔" },
      { fraction: 3/4, label: "3/4", vulgar: "¾" },
      { fraction: 5/6, label: "5/6", vulgar: "⅚" },
    ];

    const parsed = parseFloat(num);
    if (isNaN(parsed)) return num;

    const fractionTolerance = 0.008; // Tolerance to catch common rounding (e.g., 0.33 → 1/3, 0.66 → 2/3)
    const wholeTolerance = 0.02; // Larger tolerance for whole numbers (e.g., 0.99 → 1, 1.98 → 2)
    const whole = Math.floor(parsed);
    const decimal = parsed - whole;

    // Check if it's basically a whole number (including cases like 0.99 or 1.01)
    if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
      return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
    }

    // Check for fraction match
    const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

    if (match) {
      const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
      if (whole > 0) {
        return <>{whole} {fractionSpan}</>;
      }
      return fractionSpan;
    }

    // Fall back to decimal, cleaned up
    const rounded = parsed.toFixed(3);
    return rounded.replace(/\.00$/, '');
  }, []);

  // Initial sorting function to be called only on new data
  const sortIngredients = useCallback((ingredients) => {
    // Group by food group first
    const grouped = ingredients.reduce((acc, ingredient) => {
      const group = ingredient.primary_food_group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push({...ingredient}); // Clone to avoid mutation issues
      return acc;
    }, {});
    
    // Sort within each group
    Object.keys(grouped).forEach(group => {
      grouped[group].sort((a, b) => {
        // First sort by purchased status (false comes before true)
        if (a.is_purchased !== b.is_purchased) {
          return a.is_purchased ? 1 : -1;
        }
        // Then sort by in-cart status (false comes before true)
        return Number(a.is_in_cart) - Number(b.is_in_cart);
      });
    });
    
    // Flatten grouped and sorted ingredients
    const sortedGroups = Object.keys(grouped).sort();
    const sortedIngredients = [];
    sortedGroups.forEach(group => {
      // Add group marker
      sortedIngredients.push({ isGroupHeader: true, groupName: group });
      // Add ingredients in this group
      sortedIngredients.push(...grouped[group]);
    });
    
    return sortedIngredients;
  }, []);

  const fetchIngredients = useCallback(async (listId) => {
    const idToUse = listId || groceryListId;
    if (!idToUse) return;
    
    try {

      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      const response = await fetchWithCSRF(`/api/v1/grocery-list/${idToUse}/app_user/${pgUser.id}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const responseData = await response.json();
      
      // Extract the grocery list details and ingredients from the nested structure
      const groceryList = responseData[0]?.data?.grocery_list;
      const groceryListIngredients = responseData[0]?.data?.grocery_list_ingredients;
      
      setGroceryListDetails(groceryList);
      
      const mappedIngredients = groceryListIngredients?.map(ingredient => ({
        ...ingredient,
        is_in_cart: ingredient.is_in_cart || false
      })) || [];
      
      // Always update the loaded id reference when fetch completes successfully
      loadedGroceryListId.current = idToUse;
      
      // Set raw ingredients data
      setRawIngredients(mappedIngredients);
      
      // Sort and create stable view of ingredients
      // This only happens when we get fresh data
      setStableIngredients(sortIngredients(mappedIngredients));
    } catch (error) {
      console.error('Error fetching grocery list ingredients:', error);
    }
  }, [pgUser.id, groceryListId, sortIngredients]);

  // Function to show toast notification
  const showGroceryListUpdatedSuccessToast = () => {
      setToastMessage("Grocery List Updated");
      setShowToast(true);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
          setShowToast(false);
      }, 3000);
  };

  useEffect(() => {
    if (groceryListId && loadedGroceryListId.current !== groceryListId) {
      fetchIngredients(groceryListId);
    }
    // Don't clear ingredients when groceryListId becomes undefined
    // This prevents the state from being cleared during re-renders
    // Only clear if we have a different valid groceryListId
  }, [fetchIngredients, groceryListId]);

  const handleIsInCart = useCallback(async (ingredientId, isInCart) => {
    // Optimistically update UI first for immediate feedback
    setRawIngredients(prev => prev.map(ingredient => 
      ingredient.id === ingredientId
      ? { ...ingredient, is_in_cart: isInCart }
      : ingredient
    ));
    
    setStableIngredients(prev => prev.map(item => 
      item.id === ingredientId
      ? { ...item, is_in_cart: isInCart }
      : item
    ));
    
    // Then make the API request
    try {
      await setGroceryListIngredientIsInCartStatus(ingredientId, isInCart, pgUser.id);
      // API call succeeded, no need to do anything else
    } catch (error) {
      console.error('Error updating ingredient cart status:', error);
      // Don't revert the UI state - keep the user's selection even if the API call fails
      // This allows the grocery list to work offline or with spotty connectivity
    }
  }, [setGroceryListIngredientIsInCartStatus, pgUser.id]);

  const handleAddToPantry = useCallback(() => {
    const checkedItems = rawIngredients.filter(ingredient => 
      ingredient.is_in_cart && !ingredient.is_purchased
    );
    if (checkedItems.length === 0) return;
    // Prepare modal state
    setPendingItems(checkedItems);
    setPendingQuantities(
      Object.fromEntries(checkedItems.map(item => [item.id, item.quantity]))
    );
    setShowConfirmModal(true);
  }, [rawIngredients]);

  const handleConfirmAddToPantry = useCallback(async () => {
    if (pendingItems.length === 0) return;

    try {
      // Create array of pantry ingredients to add in batch
      const pantryIngredients = pendingItems.map(item => ({
        app_user_id: pgUser.id,
        pantry_id: pgUser.pantry_id,
        ingredient_id: item.ingredient_id,
        unit_of_measurement: item.unit_of_measurement,
        quantity: pendingQuantities[item.id],
        purchase_date: getTodayLocalDateString()
      }));

      // Add all items to pantry in a single API call
      // Pass true for isConfirmed since user is explicitly adding these items
      const result = await addPantryIngredients(pantryIngredients, true);
      console.log("addPantryIngredients result", result);
      
      if (result) {
        try {
          // Get array of IDs to mark as purchased
          const itemIds = pendingItems.map(item => item.id);
          
          // Mark all items as purchased in a single API call
          await setMultipleGroceryListIngredientsPurchasedStatus(itemIds, true, pgUser.id);
          
          // Update raw data
          setRawIngredients(prev => prev.map(ingredient => 
            itemIds.includes(ingredient.id) 
              ? { ...ingredient, is_purchased: true }
              : ingredient
          ));
          
          // Update stable view WITHOUT reordering
          setStableIngredients(prev => prev.map(item => 
            !item.isGroupHeader && itemIds.includes(item.id) 
              ? { ...item, is_purchased: true }
              : item
          ));
        } catch (error) {
          console.error('Error marking items as purchased:', error);
          setErrorAddingToPantry(true);
        }
      }
    } catch (error) {
      console.error('Error processing batch add to pantry:', error);
      setErrorAddingToPantry(true);
    } finally {
      setShowConfirmModal(false);
    }
  }, [pendingItems, pendingQuantities, addPantryIngredients, pgUser, setMultipleGroceryListIngredientsPurchasedStatus]);

  const roundToNearestQuarter = useCallback((value) => Math.round(value * 4) / 4, []);

  const handleQuantityChange = useCallback((id, delta) => {
    setPendingQuantities(prev => {
      const raw = (parseFloat(prev[id]) || 0.25) + delta;
      const newQty = Math.max(0.25, roundToNearestQuarter(raw));
      return { ...prev, [id]: parseFloat(newQty.toFixed(3)) };
    });
  }, [roundToNearestQuarter]);

  const handleQuantityInputChange = useCallback((id, value) => {
    // Allow empty string or any numeric input while typing
    if (value === '' || value === '.') {
      setPendingQuantities(prev => ({ ...prev, [id]: value }));
      return;
    }
    
    let num = parseFloat(value);
    if (isNaN(num)) {
      // If it's not a valid number, keep the raw input for now
      setPendingQuantities(prev => ({ ...prev, [id]: value }));
      return;
    }
    
    // Store the numeric value while typing
    setPendingQuantities(prev => ({ ...prev, [id]: num }));
  }, []);

  const handleQuantityInputBlur = useCallback((id) => {
    // Apply validation when user finishes editing
    setPendingQuantities(prev => {
      const currentValue = prev[id];
      let num = parseFloat(currentValue);
      
      if (isNaN(num) || num < 0.25) {
        num = 0.25;
      }
      // Don't apply quarter rounding for manually typed values - respect user's input
      
      return { ...prev, [id]: num };
    });
    setEditingQtyId(null);
  }, []);

  const handleQuantityInputKeyDown = useCallback((e, id) => {
    if (e.key === 'Enter') {
      // Apply validation when user presses Enter
      setPendingQuantities(prev => {
        const currentValue = prev[id];
        let num = parseFloat(currentValue);
        
        if (isNaN(num) || num < 0.25) {
          num = 0.25;
        }
        // Don't apply quarter rounding for manually typed values - respect user's input
        
        return { ...prev, [id]: num };
      });
      setEditingQtyId(null);
    }
  }, []);

  // Memoize the grouped ingredients calculation
  const { groupedIngredients, groupOrder } = useMemo(() => {
    const grouped = rawIngredients.reduce((acc, ingredient) => {
      const group = ingredient.primary_food_group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(ingredient);
      return acc;
    }, {});
    return { 
      groupedIngredients: grouped,
      groupOrder: Object.keys(grouped).sort()
    };
  }, [rawIngredients]);

  // Fetch differences when component mounts or groceryListId changes
  useEffect(() => {
    const fetchDifferences = async () => {
      if (groceryListId && groceryListDetails?.meal_plan_id && pgUser?.id) {
        const diffs = await getDifferences(
          groceryListId,
          pgUser.id,
          groceryListDetails.meal_plan_id
        );
        if (diffs && diffs.length > 0) {
          setDifferences(diffs);
          setShowDifferencesModal(true);
        }
      }
    };
    //only run if grocery list not in the past (AKA grocery_list.end_date is either today or in the future)
    if (groceryListDetails?.end_date && new Date(groceryListDetails.end_date) >= new Date()) {
      fetchDifferences();
    }
  }, [groceryListId, groceryListDetails, pgUser, getDifferences]);

  const getLocalTimestamp = useCallback(() => {
    const now = new Date();
    return now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0') + ' ' +
      String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0') + ':' +
      String(now.getSeconds()).padStart(2, '0');
  }, []);


  // Add ingredient back to grocery list
  const confirmAlreadyInPantry = useCallback(async (ingredientId) => {
    try {
      
      const result = await updatePantryIngredientConfirmedStatusByIngredientIdAndPantryIdAndAppUserId(ingredientId, pgUser.pantry_id, pgUser.id, true, getLocalTimestamp());

      if (!result.ok) {
        console.log("Failed response:", result);
        throw new Error(result.error || 'Failed to update ingredient status');
      }

      // Remove from differences array
      setDifferences(prev => prev.filter(diff => diff.ingredient_id !== ingredientId));
      
      // Close modal if no differences left
      if (differences.length <= 1) {
        setShowDifferencesModal(false);
      }

    } catch (error) {
      console.error('Error confirming pantry ingredient:', error);
      setShowErrorToast(true);
    }
  }, [differences, updatePantryIngredientConfirmedStatusByIngredientIdAndPantryIdAndAppUserId, pgUser.id, getLocalTimestamp, groceryListId]);

  // Either remove from pantry or update quantity
  const handleUpdatePantryIngredient = useCallback((ingredientId) => {
    setDifferences(prev => prev.filter(diff => diff.ingredient_id !== ingredientId));
    
    // Close modal if no differences left
    if (differences.length <= 1) {
      setShowDifferencesModal(false);
    }
  }, [differences]);

  const handlePantryIngredientRemoval = useCallback(async (ingredientId) => {
    try {
      console.log("handlePantryIngredientRemoval: ", ingredientId, " pantry id: ", pgUser.pantry_id, " app user id: ", pgUser.id);
      const result = await removeIngredientFromPantry(ingredientId, pgUser.pantry_id, pgUser.id, groceryListDetails.id);
      if (!result.ok) {
        console.error("Failed response:", result.error);
        throw new Error(result.error || 'Failed to update ingredient status');
      }

      // Remove from differences array
      setDifferences(prev => prev.filter(diff => diff.ingredient_id !== ingredientId));
      
      // Close modal if no differences left
      if (differences.length <= 1) {
        setShowDifferencesModal(false);
      }

      // Refetch the grocery list in the background
      fetchIngredients(groceryListDetails.id);

      showGroceryListUpdatedSuccessToast();

    } catch (error) {
      console.error('Error removing ingredient from pantry:', error);
      setShowErrorToast(true);
    }
  }, [removeIngredientFromPantry, pgUser.id, pgUser.pantry_id, differences.length, groceryListDetails?.id, fetchIngredients]);

  // Memoize Modal component to prevent re-renders when props don't change
  const ConfirmModal = useMemo(() => (
    <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Item Quantities</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pendingItems.length === 0 ? (
          <div>No items selected.</div>
        ) : (
          <ul className="list-unstyled">
            {pendingItems.map(item => (
              <li key={item.id} className="mb-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={getIngredientImageUrl(item.ingredient_id)}
                    alt={item.ingredient_name}
                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', marginRight: '0.5rem', background: '#ffffff' }}
                    onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                  />
                  <span>
                    {item.ingredient_name} ({item.unit_of_measurement})
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => handleQuantityChange(item.id, -0.25)}
                    style={{ minWidth: '2rem', fontWeight: 'bold' }}
                  >
                    −
                  </Button>
                  {editingQtyId === item.id ? (
                    <input
                      type="number"
                      step="0.25"
                      min="0.25"
                      value={pendingQuantities[item.id]}
                      autoFocus
                      style={{ width: '3.5rem', textAlign: 'center', borderRadius: '4px', border: '1px solid #ccc' }}
                      onChange={e => handleQuantityInputChange(item.id, e.target.value)}
                      onBlur={() => handleQuantityInputBlur(item.id)}
                      onKeyDown={e => handleQuantityInputKeyDown(e, item.id)}
                    />
                  ) : (
                    <span
                      style={{ minWidth: '2.5rem', textAlign: 'center', cursor: 'pointer', borderBottom: '1px dashed #888' }}
                      onClick={() => setEditingQtyId(item.id)}
                      title="Click to edit"
                    >
                      {formatQuantity(pendingQuantities[item.id])}
                    </span>
                  )}
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => handleQuantityChange(item.id, 0.25)}
                    style={{ minWidth: '2rem', fontWeight: 'bold' }}
                  >
                    +
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isAddingToPantry}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirmAddToPantry}
          disabled={isAddingToPantry || pendingItems.length === 0}
        >
          {isAddingToPantry ? 'Adding Items to Pantry...' : 'Confirm & Add to Pantry'}
        </Button>
      </Modal.Footer>
    </Modal>
  ), [showConfirmModal, pendingItems, pendingQuantities, editingQtyId, handleQuantityChange, handleQuantityInputChange, handleQuantityInputBlur, handleQuantityInputKeyDown, handleConfirmAddToPantry, isAddingToPantry]);

  // Memoize Differences Modal component
  const DifferencesModal = useMemo(() => (
    <Modal 
      show={showDifferencesModal} 
      onHide={() => setShowDifferencesModal(false)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Double-check your pantry before you shop</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
        These ingredients were not included in your grocery list because they're already in your pantry.
        <br />
        If you need to update any of these pantry items, click "No" to remove them from your pantry or modify the quantity in your pantry.
        </p>
        {differences.length === 0 ? (
          <div>No ingredients to review.</div>
        ) : (
          <ul className="list-unstyled">
            {differences.map(diff => (
              <li key={diff.ingredient_id} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <img
                        src={getIngredientImageUrl(diff.ingredient_id)}
                        alt={diff.ingredient_name}
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', marginRight: '1rem' }}
                        onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                      />
                      <div>
                        <p className="mb-0" style={{ maxWidth: '470px', display: 'block', fontSize: '0.9rem' }}>Do you still have <b>{formatQuantity(diff.pantry_quantity)} {diff.unit_of_measurement}</b> of <b>{diff.ingredient_name}</b> in your pantry?</p>
                        <small className="text-muted">
                          {formatQuantity(diff.original_quantity)} {diff.unit_of_measurement} required for your meals
                        </small>
                        
                        {/* {diff.consolidated_quantity > 0 && (
                          <small>
                            <br />{diff.consolidated_quantity} {diff.unit_of_measurement} already on your grocery list
                          </small>
                        )} */}
                        <br />
                      </div>
                    </div>
                    <div className="ms-0 ps-2">
                      <ul className="mb-0 mt-1 list-unstyled">
                        {diff.meal_sources.map(source => (
                          <li key={source.meal_id}>
                            {/* <span className="badge text-dark text-wrap" style={{ backgroundColor: '#e0e0e0' }}>
                              {source.recipe_name} ({source.quantity} {diff.unit_of_measurement})
                            </span> */}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 ms-3">
                                          <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => confirmAlreadyInPantry(diff.ingredient_id)}
                        style={{ borderColor: '#52cd92' }}
                      >
                        Yes
                      </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handlePantryIngredientRemoval(diff.ingredient_id)}
                    >
                      No
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDifferencesModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  ), [showDifferencesModal, differences, confirmAlreadyInPantry, handleUpdatePantryIngredient, handlePantryIngredientRemoval]);

  

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-8 col-xl-12">
          {!isInPast && (
          <div className="d-flex flex-column align-items-center mb-3">
            <Button 
              onClick={handleAddToPantry}
              disabled={isAddingToPantry || !rawIngredients.some(i => i.is_in_cart && !i.is_purchased) || isInPast}
              className="mb-1"
              style={{
                borderColor: '#52cd92',
              }}
            >
              Add Items to Pantry
            </Button>
            {errorAddingToPantry && (
              <div className="alert alert-danger mt-2 px-2 py-2" role="alert">
                Error adding items to pantry. Please try again.
              </div>
            )}
          </div>
          )}

          {!isInPast && (
            <small className="text-muted">
                  💡 Check the boxes next to items you have purchased, then click the button above to add them to your pantry
            </small>
          )}
          <div className="d-flex justify-content-center mb-3">
          </div>
          <div className="d-flex justify-content-between align-items-center px-5 mb-2" style={{ width: '100%', maxWidth: '600px', margin: '0 auto', fontWeight: 600, color: '#555', fontSize: '1rem' }}>
            <span>Item</span>
            <span>Quantity</span>
          </div>
          <hr style={{ width: '100%', maxWidth: '600px', margin: '0 auto 0.5rem auto', borderTop: '3px solid black' }} />
          <ul className="list-unstyled">
            {(() => {
              // Show loading state if we have a groceryListId but no ingredients yet
              if (groceryListId && stableIngredients.length === 0) {
                if (loadedGroceryListId.current !== groceryListId) {
                  return (
                    <div className="text-center">
                      <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading grocery list...</p>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center">
                      <p className="mt-2">No items on your list!</p>
                    </div>
                  );
                }
              }
              
              return stableIngredients.map((item, index) => {
                if (item.isGroupHeader) {
                  // Render group header
                  return (
                    <li key={`group-${item.groupName}`}>
                      <h5 style={{ marginTop: '0.5rem', marginBottom: '0.5rem', color: 'black', fontWeight: 700 }}>
                        {item.groupName}
                      </h5>
                    </li>
                  );
                } else {
                  // Render ingredient item
                  return (
                    <div 
                      key={item.id} 
                      className={`d-flex align-items-center py-2 px-1 border-bottom ${item.is_purchased ? 'text-muted' : ''}`}
                      style={{ 
                        width: '100%',
                        maxWidth: '600px',
                        margin: '0 auto',
                        opacity: item.is_purchased ? '0.6' : isInPast ? '0.6' : '1'
                      }}
                    >
                      <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                        <img
                          src={getIngredientImageUrl(item.ingredient_id)}
                          alt={item.ingredient_name}
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', marginRight: '0.5rem', background: '#ffffff' }}
                          onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                        />
                        <Form.Check
                          type="checkbox"
                          checked={item.is_purchased || item.is_in_cart}
                          onChange={(e) => handleIsInCart(item.id, e.target.checked)}
                          className="custom-checkbox"
                          disabled={item.is_purchased || isInPast}
                          style={{ 
                            cursor: (item.is_purchased || isInPast) ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <IngredientName 
                          lineThrough={item.is_purchased || item.is_in_cart}
                          textKey={item.ingredient_name + (item.notes || '')}
                          onClick={() => {
                            if (!item.is_purchased && !isInPast) {
                              const newIsInCartValue = !(item.is_purchased || item.is_in_cart);
                              // Apply optimistic update directly here
                              handleIsInCart(item.id, newIsInCartValue);
                            }
                          }}
                          disabled={item.is_purchased || isInPast}
                        >
                          {item.ingredient_name}
                          {item.notes && <small className="text-muted ms-2">({item.notes})</small>}
                        </IngredientName>
                      </div>
                      <div 
                        style={{ 
                          minWidth: '90px', 
                          textAlign: 'right', 
                          fontWeight: 500,
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => {
                          setSelectedIngredientForConversion(item);
                          setShowMassVolumeConversionPopup(true);
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Click to see unit conversions"
                      >
                        {formatQuantity(item.quantity)} {item.unit_of_measurement}
                      </div>
                    </div>
                  );
                }
              });
            })()}
          </ul>
            
          {groceryListDetails && (
            <div className="small text-muted">
              {groceryListDetails.total_items} Items | 
              Estimated Cost: ${(groceryListDetails.estimated_total_cost_in_cents / 100).toFixed(2)}
            </div>
          )}
        </div>
      </div>
      {/* Confirm Modal remains unchanged */}
      {DifferencesModal}
      {ConfirmModal}
      
      {/* Mass/Volume Conversion Popup */}
      {selectedIngredientForConversion && (
        <MassVolumeConversionPopup
          show={showMassVolumeConversionPopup}
          onHide={() => {
            setShowMassVolumeConversionPopup(false);
            setSelectedIngredientForConversion(null);
          }}
          initialUnit={selectedIngredientForConversion.unit_of_measurement}
          initialQuantity={selectedIngredientForConversion.quantity}
          density={selectedIngredientForConversion.density_g_per_cup}
          ingredientName={selectedIngredientForConversion.ingredient_name}
          ingredientId={selectedIngredientForConversion.ingredient_id}
        />
      )}
    
      {/* Error Toast - Outside main component div to ensure it's always on top */}
      <Toast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#f8d7da',
          borderColor: '#f5c2c7',
          color: '#842029',
          zIndex: 2000,
          minWidth: '250px'
        }}
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>
          An error occurred. Please refresh the page and try again.
        </Toast.Body>
      </Toast>

        {/* Toast Notification */}
        <Toast 
            show={showToast} 
            onClose={() => setShowToast(false)}
            style={{
                position: 'fixed',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999,
                backgroundColor: '#52cd92',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                width: '50%'
            }}
        >
            <Toast.Header style={{ 
                backgroundColor: '#52cd92', 
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                width: '100%'
            }}>
                <strong style={{ color: 'white' }}>{toastMessage}</strong>
            </Toast.Header>
        </Toast>

    </div>

    
  );
};
export default GroceryList;